/**
 * ML Enhancement API
 * Handles communication with ML DEM enhancement service
 */

import axios from './axiosConfig';

// Configuration
const CONFIG = {
  API_KEY: process.env.REACT_APP_API_KEY || 'frac_d48567809b669980dc4fd133a106fee041784bac36b00dee6ab92a2326f9aad5',
  TIMEOUT_MS: 125000, // 125s (backend has 120s timeout for CPU processing)
  ML_SERVICE_TIMEOUT_MS: 120000
};

// 🔧 Downsample large elevation grids before sending to ML
const downsampleElevationData = (elevationData) => {
  try {
    const grid =
      elevationData?.grid ||
      elevationData?.data ||
      elevationData;

    if (!Array.isArray(grid) || !Array.isArray(grid[0])) {
      console.warn('⚠️ Elevation grid not array, skipping downsampling');
      return elevationData;
    }

    const rows = grid.length;
    const cols = grid[0].length;

    const MAX_SIZE = 128;

    // 🚨 HARD LIMIT ONLY (no fancy logic)
    const reducedGrid = grid
      .slice(0, MAX_SIZE)
      .map(row => row.slice(0, MAX_SIZE));

    console.log(
      `🚀 HARD LIMIT grid: ${rows}x${cols} → ${reducedGrid.length}x${reducedGrid[0].length}`
    );

    return {
      ...elevationData,
      grid: reducedGrid,
      data: reducedGrid
    };

  } catch (err) {
    console.warn('⚠️ Downsampling failed, using original grid', err);
    return elevationData;
  }
};

// Error message mapping
const ERROR_MESSAGES = {
  503: {
    message: 'ML service is unavailable. Please ensure the Python ML service is running.',
    canRetry: true
  },
  504: {
    message: 'Enhancement timeout. CPU processing takes longer - try a smaller area or wait for processing to complete.',
    canRetry: true
  },
  429: {
    message: 'Rate limit exceeded. Please wait before trying again.',
    canRetry: false
  },
  default: {
    message: 'Enhancement failed. Please try again.',
    canRetry: true
  }
};

/**
 * Get error details for HTTP status code
 * @private
 */
const getErrorDetails = (status, errorData) => {
  const errorConfig = ERROR_MESSAGES[status] || ERROR_MESSAGES.default;
  
  return {
    error: errorData.error || 'Enhancement failed',
    message: errorData.message || errorConfig.message,
    canRetry: errorConfig.canRetry,
    fallbackAvailable: errorData.fallback_available !== false
  };
};

/**
 * Check ML service health
 * @returns {Promise<object>} Health status
 */
export const checkMLServiceHealth = async () => {
  try {
    const response = await axios.get('/api/v1/ml-service/health', {
      headers: {
        'X-API-Key': CONFIG.API_KEY
      }
    });
    
    return {
      success: true,
      ...response.data.mlService
    };
  } catch (error) {
    console.error('❌ ML service health check failed:', error);
    return {
      success: false,
      healthy: false,
      error: error.response?.data?.message || error.message
    };
  }
};

/**
 * Enhance DEM and analyze route
 * @param {object} route - Route data
 * @param {object} elevationData - 30m elevation data
 * @param {object} vehicleData - Vehicle information
 * @returns {Promise<object>} Enhanced analysis results
 */
export const enhanceAndAnalyze = async (route, elevationData, vehicleData) => {
  try {
    console.log('🔬 Requesting ML enhancement...');
    console.log('   Route distance:', (route.distance / 1000).toFixed(1), 'km');
    
    // Send full grid — backend handles downsampling with proper geographic alignment
    const response = await axios.post(
      '/api/v1/terrain/enhance-and-analyze',
      {
        route,
        elevationData,
        vehicleData
      },
      {
        headers: {
          'X-API-Key': CONFIG.API_KEY,
          'Content-Type': 'application/json'
        },
        timeout: CONFIG.TIMEOUT_MS
      }
    );
    
    if (response.data.success) {
      console.log('✅ ML enhancement successful');
      console.log('   Source:', response.data.source);
      console.log('   Cache hit:', response.data.cacheHit);
      
      return {
        success: true,
        ...response.data
      };
    } else {
      throw new Error(response.data.message || 'Enhancement failed');
    }
    
  } catch (error) {
    console.error('❌ ML enhancement failed:', error);
    
    const errorData = error.response?.data || {};
    const status = error.response?.status;
    
    return {
      success: false,
      ...getErrorDetails(status, errorData)
    };
  }
};

/**
 * Get ML service metrics
 * @returns {Promise<object>} Service metrics
 */
export const getMLServiceMetrics = async () => {
  try {
    const response = await axios.get('/api/v1/ml-service/metrics', {
      headers: {
        'X-API-Key': CONFIG.API_KEY
      }
    });
    
    return {
      success: true,
      ...response.data.metrics
    };
  } catch (error) {
    console.error('❌ Failed to get ML service metrics:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

